import { useTranslation } from 'react-i18next';
import { IconButton, Typography } from '@mui/material/';
import { ChevronLeft, ChevronRight, KeyboardDoubleArrowLeft, KeyboardDoubleArrowRight } from '@mui/icons-material';
import { PickersRangeCalendarHeaderProps } from '@mui/x-date-pickers-pro/PickersRangeCalendarHeader';
import { addMonths, addYears, startOfMonth, endOfMonth, isAfter, isBefore } from 'date-fns';

import { getLocale } from '@/components/common/translateFunctions';
import { datePicker } from '@/utilities/dateUtils';

import './DateTimeSelect.css';

export function CustomCalendarHeader(props: PickersRangeCalendarHeaderProps) {
  const { t, i18n } = useTranslation();
  const locale = getLocale(i18n.language);

  const {
    currentMonth,
    month,
    calendars = 2,
    monthIndex = 0,
    onMonthChange,
    disableFuture,
    disablePast,
    minDate,
    maxDate,
  } = props;

  const today = new Date();
  // anchor az egész range-hez
  const anchor = currentMonth ?? month ?? today;
  // label az adott panelhez
  const thisMonth = month ?? anchor;

  let upperLimit: Date | undefined = maxDate ?? undefined;
  if (disableFuture && !upperLimit) upperLimit = today;

  let lowerLimit: Date | undefined = minDate ?? undefined;
  if (disablePast && !lowerLimit) lowerLimit = today;

  // vissza: próbáljuk calendars-t (2 hónap), ha az túlmenne az aktuálison csak 1 hónapot
  let backStepMonths: number | null = calendars;

  if (lowerLimit) {
    const candidate2 = addMonths(anchor, -calendars);
    if (isBefore(startOfMonth(candidate2), endOfMonth(lowerLimit))) {
      const candidate1 = addMonths(anchor, -1);
      if (isBefore(startOfMonth(candidate1), endOfMonth(lowerLimit))) {
        backStepMonths = null; // már nem léphetünk előre
      } else {
        backStepMonths = 1; // csak 1 hónapot lépünk előre
      }
    }
  }

  // előre: próbáljuk calendars-t (2 hónap), ha az túlmenne az aktuálison csak 1 hónapot
  let forwardStepMonths: number | null = calendars;

  if (upperLimit) {
    const candidate2 = addMonths(anchor, calendars);
    if (isAfter(startOfMonth(candidate2), endOfMonth(upperLimit))) {
      const candidate1 = addMonths(anchor, 1);
      if (isAfter(startOfMonth(candidate1), endOfMonth(upperLimit))) {
        forwardStepMonths = null; // már nem léphetünk előre
      } else {
        forwardStepMonths = 1; // csak 1 hónapot lépünk előre
      }
    }
  }

  // hónapváltás
  const nextMonthDate = forwardStepMonths === null ? anchor : addMonths(anchor, forwardStepMonths);
  const prevMonthDate = backStepMonths === null ? anchor : addMonths(anchor, -backStepMonths);

  // évváltás
  const nextYearDate = addYears(anchor, 1);
  const prevYearDate = addYears(anchor, -1);

  const disableNextMonth =
    forwardStepMonths === null || (!!upperLimit && isAfter(startOfMonth(nextMonthDate), endOfMonth(upperLimit)));
  const disablePrevMonth =
    backStepMonths === null || (!!lowerLimit && isBefore(endOfMonth(prevMonthDate), startOfMonth(lowerLimit)));

  const disableNextYear = !!upperLimit && isAfter(startOfMonth(nextYearDate), endOfMonth(upperLimit));
  const disablePrevYear = !!lowerLimit && isBefore(endOfMonth(prevYearDate), startOfMonth(lowerLimit));

  const isFirst = monthIndex === 0;
  const isLast = monthIndex === calendars - 1;

  function selectNextMonth() {
    if (!disableNextMonth && isLast && forwardStepMonths !== null) {
      onMonthChange?.(nextMonthDate);
    }
  }

  function selectNextYear() {
    if (!disableNextYear && isLast) {
      onMonthChange?.(nextYearDate);
    }
  }

  function selectPreviousMonth() {
    if (!disablePrevMonth && isFirst) {
      onMonthChange?.(prevMonthDate);
    }
  }

  function selectPreviousYear() {
    if (!disablePrevYear && isFirst) {
      onMonthChange?.(prevYearDate);
    }
  }

  const headerDate = datePicker(thisMonth, locale, t);

  return (
    <div className="custom-calendar-header">
      <div>
        {isFirst && (
          <>
            <IconButton
              onClick={selectPreviousYear}
              title={t('general.dateTime.previousYear')}
              disabled={disablePrevYear}
              size="small"
              data-qa-element-id="calendar_previous_year_button"
            >
              <KeyboardDoubleArrowLeft fontSize="small" />
            </IconButton>
            <IconButton
              onClick={selectPreviousMonth}
              title={t('general.dateTime.previousMonth')}
              disabled={disablePrevMonth}
              size="small"
              data-qa-element-id="calendar_previous_month_button"
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
          </>
        )}
      </div>
      <Typography
        variant="subtitle1"
        data-qa-element-id={`calendar_header_for_${thisMonth.getFullYear()}_${thisMonth.getMonth() + 1}`}
      >
        {headerDate}
      </Typography>
      <div>
        {isLast && (
          <>
            <IconButton
              onClick={selectNextMonth}
              title={t('general.dateTime.nextMonth')}
              disabled={disableNextMonth}
              size="small"
              data-qa-element-id="calendar_next_month_button"
            >
              <ChevronRight fontSize="small" />
            </IconButton>
            <IconButton
              onClick={selectNextYear}
              title={t('general.dateTime.nextYear')}
              disabled={disableNextYear}
              size="small"
              data-qa-element-id="calendar_next_year_button"
            >
              <KeyboardDoubleArrowRight fontSize="small" />
            </IconButton>
          </>
        )}
      </div>
    </div>
  );
}
